import axios from "axios";
import { rootStore } from "@/mobx/store";
import { API_URL, RESULT_CODE } from "@/config/const";

// 토큰 갱신 중복 방지를 위한 플래그
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// 갱신 완료 후 대기 중인 요청들에게 새 토큰 전달
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// 토큰 갱신 대기 큐에 추가
const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// 토큰 형식 검증 (만료 여부는 체크하지 않음)
export const sanitizeToken = (token: string): string => {
  if (!token) return "";

  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.warn("Invalid token format");
      return "";
    }

    // 헤더와 페이로드를 디코딩하여 형식 검증만 수행
    JSON.parse(atob(parts[0]));
    JSON.parse(atob(parts[1]));

    // 형식이 유효하면 그대로 반환 (만료 여부는 별도로 체크)
    return token;
  } catch (error) {
    console.error("Token format validation error:", error);
    return "";
  }
};

// 토큰 만료 시간 체크 함수
export const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    // 토큰 만료 10분 전부터 갱신 시도 (1시간 만료 기준)
    return expirationTime - currentTime < 600;
  } catch (error) {
    console.error("Token parsing error:", error);
    return false;
  }
};

// 토큰이 유효한지 확인하는 함수
export const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    return expirationTime > currentTime;
  } catch (error) {
    console.error("Token validation error:", error);
    return false;
  }
};

// 수동 토큰 갱신
export const refreshAccessToken = async (): Promise<string> => {
  // 이미 갱신 중이면 대기
  if (isRefreshing) {
    return new Promise((resolve) => {
      addRefreshSubscriber((token: string) => {
        resolve(token);
      });
    });
  }

  isRefreshing = true;

  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    console.log("🔄 토큰 갱신 요청 시작");

    const response = await axios.post(`${API_URL}/app/v1/auth/refresh`, {
      refreshToken,
    });

    console.log("📥 토큰 갱신 응답:", response.data);

    // API 응답 성공 여부 먼저 확인
    if (response.data.result_code !== RESULT_CODE.SUCCESS) {
      console.error("❌ 토큰 갱신 실패:", response.data.result_msg);

      if (response.data.result_code === RESULT_CODE.ERROR) {
        rootStore.signOut();
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }
      throw new Error(
        response.data.result_msg || "Token refresh failed from server"
      );
    }

    // API 응답 구조 확인 및 처리
    let accessToken = null;
    let newRefreshToken = null;

    if (response.data.accessToken) {
      // 직접 accessToken이 있는 경우
      accessToken = response.data.accessToken;
      newRefreshToken = response.data.refreshToken;
      console.log("📦 직접 accessToken에서 추출");
    } else if (response.data.result_data?.accessToken) {
      // ApiResponse 구조인 경우
      accessToken = response.data.result_data.accessToken;
      newRefreshToken = response.data.result_data.refreshToken;
      console.log("📦 result_data.accessToken에서 추출");
    }

    if (!accessToken) {
      console.error("❌ 응답에 액세스 토큰 없음:", response.data);
      throw new Error("Token refresh failed - no access token in response");
    }

    // 토큰 형식 검증
    const sanitizedToken = sanitizeToken(accessToken);
    if (!sanitizedToken) {
      console.error("❌ 토큰 형식 검증 실패");
      throw new Error("Token format validation failed");
    }

    // MobX store의 setToken 메서드 사용
    rootStore.setToken(sanitizedToken);

    // 새로운 refresh token이 있다면 저장
    if (newRefreshToken) {
      const sanitizedRefreshToken = sanitizeToken(newRefreshToken);
      if (sanitizedRefreshToken) {
        localStorage.setItem("refresh_token", sanitizedRefreshToken);
        console.log("✅ 새 리프레시 토큰 저장됨");
      }
    }

    console.log("✅ 토큰 갱신 성공");

    // 대기 중인 요청들에게 새 토큰 전달
    onTokenRefreshed(sanitizedToken);
    isRefreshing = false;

    return sanitizedToken;
  } catch (error) {
    console.error("❌ 토큰 갱신 오류:", error);
    isRefreshing = false;
    refreshSubscribers = [];
    throw error;
  }
};
